import axios, { type AxiosError } from "axios";

import {
	getAccountDeletionInProgressDetail,
	refreshAccessToken,
} from "@/lib/auth";
import { useAuthStore } from "@/stores/useAuthStore";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

if (!apiBaseUrl) {
	throw new Error("Missing VITE_API_BASE_URL");
}

export const api = axios.create({
	baseURL: `${apiBaseUrl.replace(/\/+$/, "")}/api/v1`,
	headers: {
		"X-API-Key": import.meta.env.VITE_API_KEY || "",
	},
});

api.interceptors.request.use((config) => {
	const token = useAuthStore.getState().accessToken;
	if (token) {
		config.headers.set("Authorization", `Bearer ${token}`);
	} else {
		config.headers.delete("Authorization");
	}

	return config;
});

let onUnauthorized: null | (() => void) = null;
export const setOnUnauthorized = (fn: () => void) => {
	onUnauthorized = fn;
};

api.interceptors.response.use(
	(res) => res,
	async (error: AxiosError) => {
		const originalRequest = error.config;
		if (
			error.response?.status === 403 &&
			getAccountDeletionInProgressDetail(error)
		) {
			useAuthStore.getState().reset();
			onUnauthorized?.();
			return Promise.reject(error);
		}

		if (
			error.response?.status === 401 &&
			originalRequest &&
			(originalRequest as typeof originalRequest & { _retry?: boolean })._retry
		) {
			useAuthStore.getState().reset();
			onUnauthorized?.();
			return Promise.reject(error);
		}

		if (
			error.response?.status === 401 &&
			originalRequest &&
			!(originalRequest as typeof originalRequest & { _retry?: boolean })._retry
		) {
			(
				originalRequest as typeof originalRequest & { _retry?: boolean }
			)._retry = true;
			const token = await refreshAccessToken({ force: true });
			if (token) {
				originalRequest.headers.set("Authorization", `Bearer ${token}`);
				return api.request(originalRequest);
			}

			useAuthStore.getState().reset();
			onUnauthorized?.();
		}
		return Promise.reject(error);
	},
);
